import { useEffect } from 'react';

import { Text } from '@iwoca/orion-ui';
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement
} from '@stripe/react-stripe-js';
import { Input } from '@ui';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useGetPaymentCardData } from './useGetPaymentData';

export type AddPaymentCardFormData = {
    billingPostcode: string;
    automaticPayments: boolean;
};

export const AddPaymentCardForm = ({
    onFormChange
}: {
    onFormChange: (data: AddPaymentCardFormData) => void;
}) => {
    const { hasExistingCard, lastDigits, cardBrand } = useGetPaymentCardData();

    const {
        register,
        watch,
        formState: { errors }
    } = useForm<AddPaymentCardFormData>({
        defaultValues: {
            billingPostcode: '',
            automaticPayments: false
        },
        mode: 'onChange'
    });

    const billingPostcode = watch('billingPostcode');
    const automaticPayments = watch('automaticPayments');

    // Update parent component whenever form values change
    useEffect(() => {
        onFormChange({ billingPostcode, automaticPayments });
    }, [billingPostcode, automaticPayments, onFormChange]);

    if (hasExistingCard) {
        return (
            <>
                <Text className="my-m text-2xl font-bold">
                    <FormattedMessage
                        id="_.credit_card.onboarding.payment_method.card_edit_title"
                        defaultMessage="Edit your payment card"
                    />
                </Text>
                <div className="mb-4 rounded-md bg-blue-50 p-4 flex flex-row gap-x-m">
                    <Text className="text-s font-med text-primary-10">
                        {cardBrand}
                    </Text>
                    <Text className="text-sm text-blue-900">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.ending_card_info"
                            defaultMessage="Ending **** {last4}"
                            values={{
                                last4: lastDigits
                            }}
                        />
                    </Text>
                </div>
            </>
        );
    }

    return (
        <div>
            <Text className="my-m text-2xl font-bold">
                <FormattedMessage
                    id="_.credit_card.onboarding.payment_method.debit_card_title"
                    defaultMessage="Payment debit card"
                />
            </Text>

            {/* Labels row */}
            <div className="mb-2 gap-3 grid grid-cols-12">
                <div className="col-span-5">
                    <Text className="my-s text-s font-med">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.card_number_label"
                            defaultMessage="Card number"
                        />
                    </Text>
                </div>
                <div className="col-span-2">
                    <Text className="my-s text-s font-med">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.expiry_label"
                            defaultMessage="Expiry"
                        />
                    </Text>
                </div>
                <div className="col-span-2">
                    <div className="gap-1 flex items-center">
                        <Text className="my-s text-s font-med">
                            <FormattedMessage
                                id="_.credit_card.onboarding.payment_method.cvv_label"
                                defaultMessage="CVV"
                            />
                        </Text>
                        <span className="h-5 w-5 bg-blue-600 flex items-center justify-center rounded-full text-xs text-white">
                            <FormattedMessage
                                id="_.credit_card.onboarding.payment_method.cvv_help_icon"
                                defaultMessage="?"
                            />
                        </span>
                    </div>
                </div>
                <div className="col-span-3">
                    <Text className="my-s text-s font-med">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.billing_postcode_label"
                            defaultMessage="Billing postcode"
                        />
                    </Text>
                </div>
            </div>

            {/* Inputs row */}
            <div className="mb-6 grid grid-cols-12 gap-m">
                {/* Card number */}
                <div className="col-span-5">
                    <CardNumberElement
                        options={{
                            style: {
                                base: {
                                    fontSize: '16px',
                                    color: '#1a1a1a',
                                    '::placeholder': {
                                        color: '#9ca3af'
                                    }
                                }
                            }
                        }}
                    />
                </div>

                {/* Expiry */}
                <div className="col-span-2">
                    <CardExpiryElement
                        options={{
                            style: {
                                base: {
                                    fontSize: '16px',
                                    color: '#1a1a1a',
                                    '::placeholder': {
                                        color: '#9ca3af'
                                    }
                                }
                            }
                        }}
                    />
                </div>

                {/* CVV */}
                <div className="col-span-2">
                    <CardCvcElement
                        options={{
                            style: {
                                base: {
                                    fontSize: '16px',
                                    color: '#1a1a1a',
                                    '::placeholder': {
                                        color: '#9ca3af'
                                    }
                                }
                            }
                        }}
                    />
                </div>

                {/* Billing postcode */}
                <div className="col-span-3">
                    <Input
                        {...register('billingPostcode', {
                            required: 'Billing postcode is required',
                            pattern: {
                                value: /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i,
                                message: 'Please enter a valid UK postcode'
                            }
                        })}
                        type="text"
                        className="h-[50px]"
                    />
                    {errors.billingPostcode && (
                        <Text className="mt-1 text-sm text-error-50">
                            {errors.billingPostcode.message}
                        </Text>
                    )}
                </div>
            </div>

            <Text className="text-xs text-structure-40">
                <FormattedMessage
                    id="_.credit_card.onboarding.payment_method.automatic_payments_description"
                    defaultMessage="By adding these details you agree to automatic payments being made each month from the debit card specified for the amount selected."
                />
            </Text>
        </div>
    );
};
